import * as React from 'react'
import { useHistory, useLocation } from 'react-router'
import { useMutation } from '@apollo/client'
import { useAuth } from '../../../../client/components/AuthProvider/AuthProvider'
import { AuthForm, AuthFormSubmitValues } from '@local/do-secundo-auth-form'
import { PoweredByToastModal } from '../../../../client/components/PoweredByToastModal/PoweredByToastModal'
import { SignUpPrompt } from '@local/do-secundo-identity-prompts'
import { dataByTypename } from '../../../../client/utils/apollo-helpers'
import { TermsOfService } from '@local/do-secundo-terms-of-service'

import {
  FROM_LOCATION,
  START_PROFILE_CREATION,
  useIsPasswordlessAuthEnabled
} from '@local/do-secundo-passwordless-authentication'
import { getRawPhoneNumberWithCountryCode } from '@local/do-secundo-form-utils'
import { createAccountValidationSchema } from '../../../../client/utils/form-schemas'
import {
  deleteTokens,
  accountLinkingStorage
} from '../../../../client/apollo/authentication/authentication-helpers'

type ModalCopy = {
  header?: string
  description?: string
  from?: FROM_LOCATION
}

type RouterState = { [key: string]: ModalCopy }

const getModalCopy = (state: RouterState) => {
  if (state.loyaltyAccountCreationInput) {
    return {
      header: 'Create a Toast Account to earn rewards',
      description:
        'Earn points towards rewards and get your food faster. Save order info and payment details across all of your favorite restaurants with a Toast Account.'
    }
  }
  if (state?.from === FROM_LOCATION.legacyLogin) {
    return {
      header: 'Log in quicker',
      description:
        'Enter your phone number and use it to log in without a password next time.'
    }
  }
  return {
    header: 'Create Account',
    description:
      'We will send a one-time verification code to your phone number to complete your Toast Account.'
  }
}

export interface CreatePasswordlessAccountModalProps {
  onClose: () => void
  buttonText: string
  includeEmail?: boolean
  handleSubmitError?: (error: { Error: string }) => void
}

export const CreatePasswordlessAccountModal = ({
  onClose,
  handleSubmitError,
  buttonText = 'Next',
  includeEmail = true
}: CreatePasswordlessAccountModalProps) => {
  const history = useHistory()
  const { state = {} } = useLocation()
  const { header, description } = getModalCopy(state)
  const [startProfileCreation] = useMutation(START_PROFILE_CREATION)
  const { setUnverifiedCustomer } = useAuth()
  const { UNIFIED_CREATE_ACCOUNT_ENABLED } = useIsPasswordlessAuthEnabled()

  const handleSubmit = async (values: AuthFormSubmitValues) => {
    const input = {
      variables: {
        input: {
          ...values,
          source: 'ONLINE_ORDERING',
          loyaltyAccountCreationInput: state.loyaltyAccountCreationInput,
          basicInfo: state.basicInfo,
          phone: getRawPhoneNumberWithCountryCode(values.phone)
        }
      }
    }
    try {
      const result = await startProfileCreation(input)
      const { StartProfileCreationResponse, StartProfileCreationError } =
        dataByTypename(result.data.startProfileCreation)

      if (StartProfileCreationError) {
        throw Error(StartProfileCreationError.message)
      }

      if (StartProfileCreationResponse) {
        setUnverifiedCustomer({
          guid: StartProfileCreationResponse.guestGuid,
          signupSource:
            state.signupSource || 'MANUAL_CREATE_PASSWORDLESS_ACCOUNT'
        })
      }

      const findFromLocation = () => {
        if (state.loyaltyAccountCreationInput) {
          return FROM_LOCATION.loyaltyCreate
        } else {
          if (UNIFIED_CREATE_ACCOUNT_ENABLED) {
            return FROM_LOCATION.loginFaster
          } else {
            return FROM_LOCATION.create
          }
        }
      }

      history.push('?mode=sms', {
        from: findFromLocation(),
        guestPhone: values.phone
      })
    } catch (error) {
      throw error
    }
  }

  const phone = state.guestPhone || state.basicInfo?.phone || ''
  const email = state.email || ''

  // we need to delete accountLinkingStorage because they may have abandoned
  // the account linking flow previously and now their credentials are out of date
  // and will result in throwing an InvalidJWT. However if they're coming from legacyLogin
  // they will need those credentials and have just acquired them.
  React.useEffect(() => {
    if (state?.from !== FROM_LOCATION.legacyLogin) {
      deleteTokens(accountLinkingStorage)
    }
  }, [])

  return (
    <PoweredByToastModal
      onClose={onClose}
      header={
        <>
          <h2 className='mx-0 mt-0 mb-3 type-headline-4'>{header}</h2>
          <p className='type-default text-default'>{description}</p>
        </>
      }
    >
      <div className='m-auto' data-testid='sign-up-prompt-container'>
        <SignUpPrompt
          form={
            <AuthForm
              hasEmail={Boolean(email)}
              handleSubmit={handleSubmit}
              initialValues={{ phone, email }}
              validationSchema={createAccountValidationSchema}
              buttonText={buttonText}
              includeEmail={includeEmail}
              handleSubmitError={handleSubmitError}
              disclaimer={<TermsOfService />}
              testId={'passwordless-create-modal'}
            />
          }
          includeLoginLink={state?.from !== FROM_LOCATION.legacyLogin}
        />
      </div>
    </PoweredByToastModal>
  )
}

CreatePasswordlessAccountModal.displayName = 'CreatePasswordlessAccountModal'
